import { GroupedPageBookmarkLessonI } from "../pages/Toolbox/components/FavoritesListGrouped";
import {
  LessonI,
  ModuleI,
  ImageI,
  PageI,
  BookmarkI,
  ResponseI,
  RecapI,
  AnalyticsEvent,
  FeedbackI,
  LessonRatingI,
  UserSegmentEnum,
  BookmarkTypeEnum,
  GeneralFeedbackTypeEnum,
  ResponsesGroupedByLessonI,
  ToolboxI,
  ModuleStatus,
  AccessCodeI,
  AccessCodeAnalytics,
  UserAnnouncementInteractionI,
  AnnouncementI,
  ToolboxProfileDefinitionI,
  ToolboxCheckinDefinitionI,
  ToolboxQuestionI,
  ToolboxPlanI,
  UserToolboxProfileI,
  ToolboxAnswers,
} from "../types";
import { APIError } from "../utils/ErrorHandling";
import { UserProfile } from "../types/rbac";
import {
  CreateUpdateBookmarkRequest,
  CreateUpdateLessonRequest,
  GetImagesPaginatedResponse,
  CreateFeedbackRequest,
  FeedbackCountResponse,
  FeedbackFilters,
  GeneralFeedbackAnalyticsParams,
  SignUpUserRequest,
  UpdateLegalAcceptanceRequest,
  LegalComplianceResponse,
  LegalAcceptanceResponse,
  TutorialStatusResponse,
  TutorialViewedResponse,
  CreateErrorReportRequest,
  ErrorReportI,
  ErrorReportsResponse,
  AnnouncementFilters,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
  GeneralFeedbackI,
} from "./types";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

/**
 * Appends optional query parameters to a URL object
 * Only appends parameters that are defined and not empty
 */
function appendOptionalParams(
  url: URL,
  params: Record<string, string | undefined | null>,
): void {
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.append(key, value);
    }
  });
}

async function get<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
  });
  if (response.ok) {
    return response.json() as Promise<T>;
  }
  const errorData = await response.json();

  throw new APIError(
    errorData?.message || "An unknown error occurred",
    response.status,
    response.statusText,
    url,
  );
}

export async function post<T>(
  url: string,
  body: string | FormData,
  headers?: HeadersInit,
): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    body,
    headers: headers || {},
    credentials: "include",
  });
  if (response.ok) {
    return response.json() as Promise<T>;
  }
  // Parse error response body before throwing
  let errorMessage: string;
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || `Error fetching ${url}`;
  } catch (e) {
    // If response is not valid JSON, fallback to text
    const errorText = await response.text();
    errorMessage = errorText || `Error fetching ${url}`;
  }
  throw new APIError(
    errorMessage || "An unknown error occurred",
    response.status,
    response.statusText,
    url,
  );
}

export async function put<T>(url: string, body: string | FormData): Promise<T> {
  const response = await fetch(url, {
    method: "PUT",
    body,
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });
  if (response.ok) {
    return response.json() as Promise<T>;
  }
  const errorData = await response.json();
  throw new APIError(
    errorData?.message || "An unknown error occurred",
    response.status,
    response.statusText,
    url,
  );
}

export async function patch<T>(url: string, body: string): Promise<T> {
  const response = await fetch(url, {
    method: "PATCH",
    body,
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });
  if (response.ok) {
    return response.json() as Promise<T>;
  }
  let errorMessage: string;
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || `Error fetching ${url}`;
  } catch (e) {
    const errorText = await response.text();
    errorMessage = errorText || `Error fetching ${url}`;
  }
  throw new APIError(
    errorMessage || "An unknown error occurred",
    response.status,
    response.statusText,
    url,
  );
}

export async function deleteItem(url: string): Promise<void> {
  const response = await fetch(url, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new APIError(
      errorData?.message || "An unknown error occurred",
      response.status,
      response.statusText,
      url,
    );
  }
}

export async function logout() {
  return post<{ message: string }>(
    `${apiBaseUrl}/auth/logout`,
    JSON.stringify({}),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function getAuthStatus() {
  return get<{
    authenticated: boolean;
    user: {
      sub: string;
      name: string;
      email: string;
      role: string;
    };
  }>(`${apiBaseUrl}/auth/me`);
}

export async function getTopics(title?: string, status?: ModuleStatus) {
  const url = new URL(`${apiBaseUrl}/topics`);
  if (title) {
    url.searchParams.append("title", title);
  }
  if (status !== undefined) {
    url.searchParams.append("status", status);
  }
  return get<ModuleI[]>(url.toString());
}

export async function getTopic(id: string) {
  return get<ModuleI>(`${apiBaseUrl}/topics/${id}`);
}

export async function createTopic(body: ModuleI) {
  return post<ModuleI>(`${apiBaseUrl}/topics`, JSON.stringify(body), {
    "Content-Type": "application/json",
  });
}

export async function updateTopic(body: ModuleI) {
  return put<ModuleI[]>(`${apiBaseUrl}/topics`, JSON.stringify(body));
}

export async function deleteModule(moduleId: string) {
  return deleteItem(`${apiBaseUrl}/topics/${moduleId}`);
}

export async function checkModuleSlug(slug: string, excludeId?: string) {
  const url = new URL(`${apiBaseUrl}/topics/check-slug`);
  url.searchParams.append("slug", slug);
  if (excludeId) url.searchParams.append("excludeId", excludeId);
  return get<{ available: boolean }>(url.toString());
}

export async function getLessons(title?: string) {
  const url = new URL(`${apiBaseUrl}/sections`);
  if (title) {
    url.searchParams.append("title", title);
  }
  return get<LessonI[]>(url.toString());
}

export async function getLesson(id: string) {
  return get<LessonI>(`${apiBaseUrl}/sections/${id}`);
}

export async function createLesson(body: CreateUpdateLessonRequest) {
  return post<LessonI>(`${apiBaseUrl}/sections`, JSON.stringify(body), {
    "Content-Type": "application/json",
  });
}

export async function updateLesson(body: CreateUpdateLessonRequest) {
  return put<LessonI>(`${apiBaseUrl}/sections`, JSON.stringify(body));
}

export async function deleteLesson(lessonId: string) {
  return deleteItem(`${apiBaseUrl}/sections/${lessonId}`);
}

export async function checkLessonSlug(slug: string, excludeId?: string) {
  const url = new URL(`${apiBaseUrl}/sections/check-slug`);
  url.searchParams.append("slug", slug);
  if (excludeId) url.searchParams.append("excludeId", excludeId);
  return get<{ available: boolean }>(url.toString());
}

export async function getUnlinkedLessons() {
  return get<LessonI[]>(`${apiBaseUrl}/sections/unlinked`);
}

export async function getPages(title?: string) {
  const url = new URL(`${apiBaseUrl}/pages`);
  if (title) {
    url.searchParams.append("title", title);
  }
  return get<PageI[]>(url.toString());
}

export async function getPage(id: string) {
  return get<PageI>(`${apiBaseUrl}/pages/${id}`);
}

export async function deletePage(pageId: string) {
  return deleteItem(`${apiBaseUrl}/pages/${pageId}`);
}

export async function getUnlinkedPages() {
  return get<PageI[]>(`${apiBaseUrl}/pages/unlinked`);
}

export async function getDeletedPages() {
  return get<PageI[]>(`${apiBaseUrl}/pages/deleted`);
}

export async function restorePage(pageId: string) {
  return post<{ message: string; pageId: string }>(
    `${apiBaseUrl}/pages/${pageId}/restore`,
    JSON.stringify({}),
  );
}

export async function getArchivedPages() {
  return get<PageI[]>(`${apiBaseUrl}/pages/archived`);
}

export async function archivePage(pageId: string) {
  return put<{ message: string; pageId: string; archivedAt: string }>(
    `${apiBaseUrl}/pages/${pageId}/archive`,
    JSON.stringify({}),
  );
}

export async function unarchivePage(pageId: string) {
  return put<{ message: string; pageId: string }>(
    `${apiBaseUrl}/pages/${pageId}/unarchive`,
    JSON.stringify({}),
  );
}

export async function getPageContainedLessons(pageId: string) {
  return get<LessonI[]>(`${apiBaseUrl}/pages/${pageId}/sections`);
}

export async function getLessonsContainedTopics(lessonId: string) {
  return get<ModuleI[]>(`${apiBaseUrl}/sections/${lessonId}/topics`);
}

export async function getRecaps(lessonId?: string) {
  const url = new URL(`${apiBaseUrl}/recaps`);
  if (lessonId) {
    url.searchParams.append("lesson", lessonId);
  }
  return get<RecapI[]>(url.toString());
}

export async function getRecap(id: string) {
  return get<RecapI>(`${apiBaseUrl}/recaps/${id}`);
}

export async function createOrGetRecap(body: RecapI) {
  return post<RecapI>(`${apiBaseUrl}/recaps`, JSON.stringify(body), {
    "Content-Type": "application/json",
  });
}

export async function updateRecap(body: RecapI) {
  return put<RecapI>(`${apiBaseUrl}/recaps`, JSON.stringify(body));
}

export async function deleteRecap(id: string) {
  return deleteItem(`${apiBaseUrl}/recaps/${id}`);
}

export async function deleteAllRecaps() {
  return deleteItem(`${apiBaseUrl}/recaps`);
}

export async function getImages(imageType: string = "") {
  return get<ImageI[]>(`${apiBaseUrl}/images?imageType=${imageType}`);
}

export async function getImageById(imageId: string) {
  return get<ImageI>(`${apiBaseUrl}/images/${imageId}`);
}

export async function getImagesPaginated(
  imageType: string = "",
  q: string = "",
  page: number = 1,
  limit: number = 10,
  extension: string = "",
) {
  return get<GetImagesPaginatedResponse>(
    `${apiBaseUrl}/images/paginated?imageType=${imageType}&q=${q}&page=${page}&limit=${limit}&extension=${extension}`,
  );
}

export async function createImage(body: FormData) {
  return post<ImageI>(`${apiBaseUrl}/images`, body);
}

export async function updateImage(id: string, body: FormData) {
  return post<ImageI>(`${apiBaseUrl}/images/${id}`, body);
}

export async function deleteImage(id: string) {
  return deleteItem(`${apiBaseUrl}/images/${id}`);
}

export async function getResponses() {
  return get<ResponseI[]>(`${apiBaseUrl}/responses`);
}

export async function getResponsesGrouped(moduleId?: string) {
  const params = moduleId ? `?moduleId=${moduleId}` : "";
  return get<ResponsesGroupedByLessonI[]>(
    `${apiBaseUrl}/responses/grouped${params}`,
  );
}

export async function getResponse(id: string) {
  return get<ResponseI>(`${apiBaseUrl}/responses/${id}`);
}

export async function createResponse(body: ResponseI) {
  return post<ResponseI>(`${apiBaseUrl}/responses`, JSON.stringify(body), {
    "Content-Type": "application/json",
  });
}

export async function deleteResponse(id: string) {
  return deleteItem(`${apiBaseUrl}/responses/${id}`);
}

export async function deleteAllResponses() {
  return deleteItem(`${apiBaseUrl}/responses`);
}

export async function getResponsesByPages(pageIds: string[]) {
  return post<Record<string, ResponseI[]>>(
    `${apiBaseUrl}/responses/pages`,
    JSON.stringify({ pageIds }),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function getBookmarks(
  type: BookmarkTypeEnum,
  moduleId?: string,
  pageId?: string,
) {
  const url = new URL(`${apiBaseUrl}/bookmarks/${type}`);
  if (moduleId) {
    url.searchParams.append("moduleId", moduleId);
  }
  if (pageId) {
    url.searchParams.append("pageId", pageId);
  }
  return get<BookmarkI[]>(url.toString());
}

export async function getPageBookmarksGroupedLessons(moduleId?: string) {
  const url = new URL(
    `${apiBaseUrl}/bookmarks/${BookmarkTypeEnum.PAGE}/grouped/lesson`,
  );
  if (moduleId) {
    url.searchParams.append("moduleId", moduleId);
  }
  return get<GroupedPageBookmarkLessonI[]>(url.toString());
}

export async function createBookmark(
  type: BookmarkTypeEnum,
  body: CreateUpdateBookmarkRequest,
) {
  return post<BookmarkI>(
    `${apiBaseUrl}/bookmarks/${type}`,
    JSON.stringify(body),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function updateBookmark(
  type: BookmarkTypeEnum,
  id: string,
  body: CreateUpdateBookmarkRequest,
) {
  return put<CreateUpdateBookmarkRequest>(
    `${apiBaseUrl}/bookmarks/${type}/${id}`,
    JSON.stringify(body),
  );
}

export async function deleteBookmark(type: BookmarkTypeEnum, id: string) {
  return deleteItem(`${apiBaseUrl}/bookmarks/${type}/${id}`);
}

export async function trackPageView(event: AnalyticsEvent) {
  return post<void>(`${apiBaseUrl}/analytics/page`, JSON.stringify(event), {
    "Content-Type": "application/json",
  });
}

export async function trackEvent(event: AnalyticsEvent) {
  return post<void>(`${apiBaseUrl}/analytics/track`, JSON.stringify(event), {
    "Content-Type": "application/json",
  });
}

export async function getLessonViews(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/section/views/`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<
    {
      _id: string;
      title: string;
      count: number;
      unqCount: number;
    }[]
  >(url.toString());
}

export async function getTopicViews(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/topic/views/`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<
    {
      _id: string;
      title: string;
      count: number;
      unqCount: number;
    }[]
  >(url.toString());
}

export async function getLessonViewsByTopicId(
  moduleId: string,
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(
    `${apiBaseUrl}/analytics/section/views/topic/${moduleId}`,
  );
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<
    {
      _id: string;
      title: string;
      count: number;
      unqCount: number;
    }[]
  >(url.toString());
}

export async function getPageViewsByTopicAndLesson(
  moduleId: string,
  lessonId: string,
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(
    `${apiBaseUrl}/analytics/page/views/topic/${moduleId}/section/${lessonId}`,
  );
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<
    {
      _id: string;
      title: string;
      count: number;
      unqCount: number;
    }[]
  >(url.toString());
}

export async function getLessonCompletedCount(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/section/count/completed/`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<
    {
      _id: string;
      title: string;
      count: number;
    }[]
  >(url.toString());
}

export async function syncUserRole() {
  return post<{ message: string }>(
    `${apiBaseUrl}/user-profiles/sync-role`,
    JSON.stringify({}),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function updateUserSegments(
  userSub: string,
  segments: string[],
): Promise<{ message: string; userProfile: UserProfile }> {
  return put<{ message: string; userProfile: UserProfile }>(
    `${apiBaseUrl}/user-profiles/${userSub}/segments`,
    JSON.stringify({ segments }),
  );
}

export async function updateDisplayName(
  displayName: string,
): Promise<{ message: string; userProfile: UserProfile }> {
  return patch<{ message: string; userProfile: UserProfile }>(
    `${apiBaseUrl}/user-profiles/display-name`,
    JSON.stringify({ displayName }),
  );
}

export async function getUserPreferences() {
  try {
    return get<{
      saveAnswersToLog: boolean;
      askEveryTime: boolean;
      dismissedTutorials: string[];
      emailConsentDate?: Date;
      emailAccountIssues?: boolean;
      emailFeedbackRequests?: boolean;
      emailNewsUpdates?: boolean;
      videoPlaybackSpeed?: number;
      autoplayVideos?: boolean;
      videoVolume?: number;
    }>(`${apiBaseUrl}/user-preferences`);
  } catch (error) {
    // If user preferences don't exist or there's an error, return defaults
    return {
      saveAnswersToLog: true, // Default to true
      askEveryTime: false,
      dismissedTutorials: [],
      videoPlaybackSpeed: 1,
      autoplayVideos: false, // Default to false
      videoVolume: 1, // Default to full volume
    };
  }
}

export async function updateUserPreferences(
  saveAnswersToLog: boolean,
  askEveryTime?: boolean,
  emailAccountIssues?: boolean,
  emailFeedbackRequests?: boolean,
  emailNewsUpdates?: boolean,
  videoPlaybackSpeed?: number,
  autoplayVideos?: boolean,
  videoVolume?: number,
) {
  const body: {
    saveAnswersToLog: boolean;
    askEveryTime?: boolean;
    emailAccountIssues?: boolean;
    emailFeedbackRequests?: boolean;
    emailNewsUpdates?: boolean;
    videoPlaybackSpeed?: number;
    autoplayVideos?: boolean;
    videoVolume?: number;
  } = {
    saveAnswersToLog,
  };
  if (askEveryTime !== undefined) {
    body.askEveryTime = askEveryTime;
  }
  if (emailAccountIssues !== undefined) {
    body.emailAccountIssues = emailAccountIssues;
  }
  if (emailFeedbackRequests !== undefined) {
    body.emailFeedbackRequests = emailFeedbackRequests;
  }
  if (emailNewsUpdates !== undefined) {
    body.emailNewsUpdates = emailNewsUpdates;
  }
  if (videoPlaybackSpeed !== undefined) {
    body.videoPlaybackSpeed = videoPlaybackSpeed;
  }
  if (autoplayVideos !== undefined) {
    body.autoplayVideos = autoplayVideos;
  }
  if (videoVolume !== undefined) {
    body.videoVolume = videoVolume;
  }

  return put<{
    message: string;
    saveAnswersToLog: boolean;
    askEveryTime: boolean;
    dismissedTutorials: string[];
    emailConsentDate?: Date;
    emailAccountIssues?: boolean;
    emailFeedbackRequests?: boolean;
    emailNewsUpdates?: boolean;
    videoPlaybackSpeed?: number;
    autoplayVideos?: boolean;
    videoVolume?: number;
  }>(`${apiBaseUrl}/user-preferences`, JSON.stringify(body));
}

export async function dismissTutorial(tutorialId: string) {
  return post<{
    message: string;
    dismissedTutorials: string[];
  }>(
    `${apiBaseUrl}/user-preferences/dismiss-tutorial`,
    JSON.stringify({
      tutorialId,
    }),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function resetTutorials() {
  return post<{
    message: string;
    dismissedTutorials: string[];
  }>(`${apiBaseUrl}/user-preferences/reset-tutorials`, JSON.stringify({}), {
    "Content-Type": "application/json",
  });
}

export async function createFeedback(body: CreateFeedbackRequest) {
  return post<FeedbackI>(`${apiBaseUrl}/feedbacks`, JSON.stringify(body), {
    "Content-Type": "application/json",
  });
}

export async function createLessonFeedback(body: FeedbackI) {
  return post<FeedbackI>(
    `${apiBaseUrl}/feedbacks/section`,
    JSON.stringify(body),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function getAllFeedback() {
  return get<FeedbackI[]>(`${apiBaseUrl}/feedbacks`);
}

export async function getAllFeedbackWithFilters(filters: FeedbackFilters = {}) {
  const url = new URL(`${apiBaseUrl}/feedbacks`);
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.append(key, value);
    }
  });

  return get<FeedbackI[]>(url.toString());
}

export async function getPageFeedback(lessonId: string, pageNumber: number) {
  // Use analytics endpoint to get feedback with segments populated
  const url = new URL(`${apiBaseUrl}/analytics/feedback/general`);
  url.searchParams.append("segment", JSON.stringify([]));
  url.searchParams.append("lessonId", lessonId);
  url.searchParams.append("pageNumber", pageNumber.toString());
  const response = await get<{ recentFeedback: FeedbackI[] }>(url.toString());
  return response.recentFeedback || [];
}

export async function getLessonFeedback(lessonId: string) {
  // Use analytics endpoint to get feedback with segments populated
  const url = new URL(`${apiBaseUrl}/analytics/feedback/general`);
  url.searchParams.append("segment", JSON.stringify([]));
  url.searchParams.append("lessonId", lessonId);
  const response = await get<{ recentFeedback: FeedbackI[] }>(url.toString());
  return response.recentFeedback || [];
}

export async function getLessonFeedbackCount(
  lessonId: string,
): Promise<FeedbackCountResponse> {
  const url = new URL(`${apiBaseUrl}/feedbacks/count`);
  url.searchParams.append("lessonId", lessonId);
  return get<FeedbackCountResponse>(url.toString());
}

export async function getPageFeedbackCount(
  lessonId: string,
  pageNumber: number,
): Promise<FeedbackCountResponse> {
  const url = new URL(`${apiBaseUrl}/feedbacks/count`);
  url.searchParams.append("lessonId", lessonId);
  url.searchParams.append("pageNumber", pageNumber.toString());
  return get<FeedbackCountResponse>(url.toString());
}

export async function getFeedbackStats() {
  return get<{
    total: number;
    byType: { _id: GeneralFeedbackTypeEnum; count: number }[];
    topPages: { _id: string; count: number; pageTitle?: string }[];
  }>(`${apiBaseUrl}/feedbacks/stats`);
}

export async function getFeedbackAnalyticsSummary(
  params: GeneralFeedbackAnalyticsParams,
) {
  const url = new URL(`${apiBaseUrl}/analytics/feedback/general`);
  url.searchParams.append("segment", JSON.stringify(params.segments));
  appendOptionalParams(url, {
    startDate: params.startDate,
    endDate: params.endDate,
    feedbackType: params.feedbackType,
    page: params.page !== undefined ? String(params.page) : undefined,
    limit: params.limit !== undefined ? String(params.limit) : undefined,
    moduleId: params.moduleId,
    lessonId: params.lessonId,
  });

  return get<{
    total: number;
    totalCount: number;
    totalPages: number;
    page: number;
    limit: number;
    byType: { _id: GeneralFeedbackTypeEnum; count: number }[];
    topPages: {
      _id: string;
      count: number;
      pageTitle?: string;
      lessonTitle?: string;
      moduleTitle?: string;
    }[];
    recentFeedback: GeneralFeedbackI[];
  }>(url.toString());
}

export async function getFeedbackByUser(userSub: string) {
  return get<FeedbackI[]>(`${apiBaseUrl}/feedbacks/user/${userSub}`);
}

export async function deleteFeedback(id: string) {
  return deleteItem(`${apiBaseUrl}/feedbacks/${id}`);
}

export async function resolveFeedback({
  feedbackId,
  isResolved,
}: {
  feedbackId: string;
  isResolved: boolean;
}) {
  return patch<FeedbackI>(
    `${apiBaseUrl}/feedbacks/${feedbackId}/resolve`,
    JSON.stringify({ isResolved }),
  );
}

export async function getLessonFeedbackById(
  lessonId: string,
  segments: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/feedback/section/${lessonId}`);
  url.searchParams.append("segment", JSON.stringify(segments));
  appendOptionalParams(url, { startDate, endDate });
  return get<{
    usefulness: {
      stats: { mean: number | null; sd: number | null };
      counts: {
        not_at_all_useful: number;
        moderately_useful: number;
        very_useful: number;
      };
      total: number;
    };
  }>(url.toString());
}

export async function getLessonRatings(
  segments: UserSegmentEnum[],
  moduleId?: string,
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/feedbacks/section-ratings`);
  if (moduleId) {
    url.searchParams.append("moduleId", moduleId);
  }
  if (segments.length > 0) {
    url.searchParams.append("segment", JSON.stringify(segments));
  }
  appendOptionalParams(url, { startDate, endDate });
  return get<LessonRatingI[]>(url.toString());
}

export async function getToolbox(moduleId: string) {
  return get<ToolboxI>(`${apiBaseUrl}/toolboxes/topic/${moduleId}`);
}

export async function getActiveToolboxes() {
  return get<{ activeToolboxes: ModuleI[]; count: number }>(
    `${apiBaseUrl}/toolboxes/active`,
  );
}

export async function updateToolboxByTopicId(
  moduleId: string,
  body: Partial<ToolboxI>,
) {
  return put<ToolboxI>(
    `${apiBaseUrl}/toolboxes/topic/${moduleId}`,
    JSON.stringify(body),
  );
}

export async function getLoginFrequency(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/auth/login-frequency`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<{
    totalLogins: number;
    totalUsers: number;
    averageLoginFrequency: number;
    minLoginCount: number;
    maxLoginCount: number;
  }>(url.toString());
}

export async function getLoginCountDistribution(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/auth/login-count-distribution`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<{
    buckets: { label: string; userCount: number }[];
    totalUsers: number;
  }>(url.toString());
}

export async function getDeviceStatistics(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/auth/device-statistics`);
  url.searchParams.append("segment", JSON.stringify(segment));
  if (startDate) {
    url.searchParams.append("startDate", startDate);
  }
  if (endDate) {
    url.searchParams.append("endDate", endDate);
  }
  return get<{
    totalLogins: number;
    deviceStats: Array<{
      deviceType: string;
      count: number;
      uniqueUserCount: number;
      percentage: number;
    }>;
    deviceBrowserStats: Array<{
      deviceType: string;
      totalCount: number;
      percentage: number;
      browsers: Array<{
        browserName: string;
        count: number;
        uniqueUserCount: number;
        percentage: number;
        percentageOfTotal: number;
      }>;
    }>;
  }>(url.toString());
}

export interface DailyActivityData {
  date: string;
  count: number;
  uniqueUserCount: number;
  hour?: number;
}

export interface DailyActivityStatsResponse {
  logins: DailyActivityData[];
  pageViews: DailyActivityData[];
  isSingleDay?: boolean;
}

export async function getDailyActivityStats(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
): Promise<DailyActivityStatsResponse> {
  const url = new URL(`${apiBaseUrl}/analytics/auth/daily-activity`);
  url.searchParams.append("segment", JSON.stringify(segment));
  url.searchParams.append(
    "timezoneOffset",
    new Date().getTimezoneOffset().toString(),
  );
  appendOptionalParams(url, { startDate, endDate });
  return get<DailyActivityStatsResponse>(url.toString());
}

export interface TutorialStatsResponse {
  tutorialViewedCount: number;
  tutorialVideoWatchedCount: number;
}

export async function getTutorialStats(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
): Promise<TutorialStatsResponse> {
  const url = new URL(`${apiBaseUrl}/analytics/tutorial/stats`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<TutorialStatsResponse>(url.toString());
}

// Signup Analytics
export interface SignupAnalyticsResponse {
  totalSignups: number;
  uniqueUsers: number;
  totalRedemptions: number;
  conversionRate: number;
  isSingleDay?: boolean;
  timeline: Array<{
    date: string;
    hour?: number;
    signups: number;
    codesEntered: number;
    emailsVerified: number;
    firstLogins: number;
  }>;
}

export async function getSignupAnalytics(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
  groupBy: "day" | "week" | "month" | "hour" = "day",
): Promise<SignupAnalyticsResponse> {
  const url = new URL(`${apiBaseUrl}/analytics/signup`);
  url.searchParams.append("segment", JSON.stringify(segment));
  url.searchParams.append("groupBy", groupBy);
  url.searchParams.append(
    "timezoneOffset",
    new Date().getTimezoneOffset().toString(),
  );
  appendOptionalParams(url, { startDate, endDate });
  return get<SignupAnalyticsResponse>(url.toString());
}

export interface SignupFunnelResponse {
  funnel: {
    stage1_codesEntered: number;
    stage2_emailsVerified: number;
    stage3_firstLogins: number;
    emailVerificationDropoffs: number;
    firstLoginDropoffs: number;
    totalDropoffs: number;
  };
  rates: {
    codeToEmailConversion: number;
    emailToLoginConversion: number;
    overallConversion: number;
  };
}

export async function getSignupFunnel(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
): Promise<SignupFunnelResponse> {
  const url = new URL(`${apiBaseUrl}/analytics/signup/funnel`);
  url.searchParams.append("segment", JSON.stringify(segment));
  url.searchParams.append(
    "timezoneOffset",
    new Date().getTimezoneOffset().toString(),
  );
  appendOptionalParams(url, { startDate, endDate });
  return get<SignupFunnelResponse>(url.toString());
}

export interface SignupsBySegmentResponse {
  segments: Array<{
    segment: string;
    signups: number;
  }>;
}

export async function getSignupsBySegment(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
): Promise<SignupsBySegmentResponse> {
  const url = new URL(`${apiBaseUrl}/analytics/signup/by-segment`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<SignupsBySegmentResponse>(url.toString());
}

export async function getToolboxUsageStats(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/toolbox/usage`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<{
    toolboxStats: {
      _id: string;
      moduleTitle: string;
      totalUsers: number;
      totalOpens: number;
      lastOpened: string;
      firstOpened: string;
    }[];
    totalOpens: number;
    totalModules: number;
  }>(url.toString());
}

export async function getToolboxOpenEvents(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/toolbox/open-events`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<{
    openEvents: {
      _id: string;
      moduleTitle: string;
      totalOpens: number;
      sources?: {
        source: string;
        openCount: number;
        uniqueUserCount: number;
      }[];
      lastOpened: string;
    }[];
    totalOpens: number;
    totalModules: number;
  }>(url.toString());
}

export async function getToolboxPageAddedEvents(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/toolbox/page-added-events`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<{
    pageAddedEvents: {
      _id: string;
      pageTitle: string;
      lessonId: string;
      lessonTitle: string;
      moduleId: string;
      moduleTitle: string;
      addCount: number;
      uniqueUserCount: number;
      lastAdded: string;
    }[];
    totalAdds: number;
    totalPages: number;
    totalUniqueUsers: number;
  }>(url.toString());
}

export async function getToolboxPageOpenedEvents(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/toolbox/page-opened-events`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<{
    pageOpenedEvents: {
      _id: string;
      pageTitle: string;
      lessonId: string;
      lessonTitle: string;
      moduleId: string;
      moduleTitle: string;
      openCount: number;
      uniqueUserCount: number;
      lastOpened: string;
    }[];
    totalOpens: number;
    totalPages: number;
    totalUniqueUsers: number;
  }>(url.toString());
}

export async function getVideoViews(
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/video/views`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<
    {
      videoUrl: string;
      lessonId: string;
      lessonTitle: string;
      moduleId: string;
      moduleTitle: string;
      pageTitle: string;
      pageIndex: number;
      pageId?: string;
      count: number;
      uniqueUserCount: number;
    }[]
  >(url.toString());
}

export async function getVideoViewsByTopicId(
  moduleId: string,
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(`${apiBaseUrl}/analytics/video/views/topic/${moduleId}`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<{
    lessons: {
      _id: string;
      title: string;
      count: number;
      unqCount: number;
    }[];
    totalUniqueUsers: number;
  }>(url.toString());
}

export async function getVideoViewsByTopicAndLesson(
  moduleId: string,
  lessonId: string,
  segment: UserSegmentEnum[],
  startDate?: string,
  endDate?: string,
) {
  const url = new URL(
    `${apiBaseUrl}/analytics/video/views/topic/${moduleId}/section/${lessonId}`,
  );
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, { startDate, endDate });
  return get<
    {
      _id: string;
      title: string;
      count: number;
      unqCount: number;
      pageIndex: number;
    }[]
  >(url.toString());
}

export async function signUpUser(body: SignUpUserRequest) {
  return post<LegalAcceptanceResponse>(
    `${apiBaseUrl}/auth/sign-up`,
    JSON.stringify(body),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function deleteAllFavorites() {
  return deleteItem(`${apiBaseUrl}/bookmarks/clear`);
}

export async function checkLegalCompliance() {
  return get<LegalComplianceResponse>(
    `${apiBaseUrl}/user-profiles/legal-compliance`,
  );
}

export async function updateLegalAcceptance(
  body: UpdateLegalAcceptanceRequest,
) {
  return post<LegalAcceptanceResponse>(
    `${apiBaseUrl}/user-profiles/legal-acceptance`,
    JSON.stringify(body),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function getLegalDates() {
  return get<{
    termsLastUpdated: string;
    privacyLastUpdated: string;
    eulaLastUpdated: string;
    termsVersion: string;
    privacyVersion: string;
    eulaVersion: string;
  }>(`${apiBaseUrl}/legal-dates`);
}

export async function getTutorialStatus() {
  return get<TutorialStatusResponse>(
    `${apiBaseUrl}/user-profiles/tutorial-status`,
  );
}

export async function updateTutorialViewed(version: string) {
  return post<TutorialViewedResponse>(
    `${apiBaseUrl}/user-profiles/tutorial-viewed`,
    JSON.stringify({ version }),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function updateTutorialVideoWatched() {
  return post<UserProfile>(
    `${apiBaseUrl}/user-profiles/tutorial-video-watched`,
    JSON.stringify({}),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function getAccessCodes(
  segment?: string,
  isActive?: boolean,
  expired?: boolean,
  search?: string,
) {
  const url = new URL(`${apiBaseUrl}/access-codes`);
  if (segment) url.searchParams.append("segment", segment);
  if (isActive !== undefined)
    url.searchParams.append("isActive", isActive.toString());
  if (expired !== undefined)
    url.searchParams.append("expired", expired.toString());
  if (search) url.searchParams.append("search", search);
  return get<AccessCodeI[]>(url.toString());
}

export async function getAccessCode(id: string) {
  return get<AccessCodeI>(`${apiBaseUrl}/access-codes/${id}`);
}

export async function createAccessCode(body: {
  code: string;
  limitCount?: number;
  segment: string;
  expirationDate?: Date;
}) {
  const payload = {
    ...body,
    expirationDate: body.expirationDate
      ? body.expirationDate.toISOString()
      : undefined,
  };

  return post<{ accessCode: AccessCodeI; success: boolean }>(
    `${apiBaseUrl}/access-codes`,
    JSON.stringify(payload),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function updateAccessCode(id: string, body: Partial<AccessCodeI>) {
  return put<AccessCodeI>(
    `${apiBaseUrl}/access-codes/${id}`,
    JSON.stringify(body),
  );
}

export async function deleteAccessCode(id: string) {
  return deleteItem(`${apiBaseUrl}/access-codes/${id}`);
}

export async function getAccessCodeAnalyticsByCode(accessCode: string) {
  return get<AccessCodeAnalytics>(
    `${apiBaseUrl}/analytics/access-code/code/${encodeURIComponent(accessCode)}`,
  );
}

export async function getAccessCodeAnalyticsById(id: string) {
  return get<AccessCodeAnalytics>(`${apiBaseUrl}/analytics/access-code/${id}`);
}

export async function getAllAnnouncements(params?: AnnouncementFilters) {
  const queryParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }

  const queryString = queryParams.toString();
  const url = `${apiBaseUrl}/announcements${queryString ? `?${queryString}` : ""}`;

  return get<AnnouncementI[]>(url);
}

export async function getAnnouncement(id: string) {
  return get<AnnouncementI>(`${apiBaseUrl}/announcements/${id}`);
}

export async function createAnnouncement(body: CreateAnnouncementRequest) {
  return post<AnnouncementI>(
    `${apiBaseUrl}/announcements`,
    JSON.stringify(body),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function updateAnnouncement(
  id: string,
  body: UpdateAnnouncementRequest,
) {
  return put<AnnouncementI>(
    `${apiBaseUrl}/announcements/${id}`,
    JSON.stringify(body),
  );
}

export async function deleteAnnouncement(id: string) {
  return deleteItem(`${apiBaseUrl}/announcements/${id}`);
}

// User interactions
export async function getUserAnnouncementInteractions() {
  return get<UserAnnouncementInteractionI[]>(
    `${apiBaseUrl}/announcements/interactions`,
  );
}

export async function trackAnnouncementView(announcementId: string) {
  return post<UserAnnouncementInteractionI>(
    `${apiBaseUrl}/announcements/interactions/${announcementId}/view`,
    JSON.stringify({}),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function dismissAnnouncement(announcementId: string) {
  return post<UserAnnouncementInteractionI>(
    `${apiBaseUrl}/announcements/interactions/${announcementId}/dismiss`,
    JSON.stringify({}),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function trackAnnouncementAction(
  announcementId: string,
  actionLabel: string,
) {
  return post<UserAnnouncementInteractionI>(
    `${apiBaseUrl}/announcements/interactions/${announcementId}/action`,
    JSON.stringify({ actionLabel }),
    {
      "Content-Type": "application/json",
    },
  );
}

// Error Report APIs
export async function createErrorReport(body: CreateErrorReportRequest) {
  return post<ErrorReportI>(
    `${apiBaseUrl}/error-reports`,
    JSON.stringify(body),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function getErrorReports(
  segment: UserSegmentEnum[],
  search?: string,
  startDate?: string,
  endDate?: string,
  statusCodes?: number[],
  includeResolved: boolean = false,
  page: number = 1,
  limit: number = 20,
) {
  const url = new URL(`${apiBaseUrl}/analytics/error-report`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, {
    search,
    startDate,
    endDate,
    statusCodes: statusCodes?.join(","),
    includeResolved: includeResolved.toString(),
    page: page.toString(),
    limit: limit.toString(),
  });
  return get<ErrorReportsResponse>(url.toString());
}

export async function getErrorReportsByStatusCode(
  segment: UserSegmentEnum[],
  search?: string,
  startDate?: string,
  endDate?: string,
  statusCodes?: number[],
  includeResolved: boolean = false,
) {
  const url = new URL(`${apiBaseUrl}/analytics/error-report/status-codes`);
  url.searchParams.append("segment", JSON.stringify(segment));
  appendOptionalParams(url, {
    search,
    startDate,
    endDate,
    statusCodes: statusCodes?.join(","),
    includeResolved: includeResolved.toString(),
  });
  return get<
    {
      _id: string;
      title: string;
      count: number;
    }[]
  >(url.toString());
}

export async function updateErrorReport(id: string, resolved: boolean) {
  return put<ErrorReportI>(
    `${apiBaseUrl}/error-reports/${id}`,
    JSON.stringify({ resolved }),
  );
}

// User deletion
export async function requestAccountDeletion() {
  return post<{
    message: string;
    isDeleted: boolean;
    deletionRequestedAt: string;
  }>(`${apiBaseUrl}/user-deletion`, JSON.stringify({}), {
    "Content-Type": "application/json",
  });
}

export async function cancelAccountDeletion() {
  return deleteItem(`${apiBaseUrl}/user-deletion`);
}

export async function getAccountDeletionStatus() {
  return get<{
    isDeleted: boolean;
    deletionRequestedAt: string | null;
    permanentDeletionDate?: string;
    daysRemaining?: number;
  }>(`${apiBaseUrl}/user-deletion`);
}

// User Email Change
// Current email verification - use dedicated email verification endpoints
export async function sendCurrentEmailVerificationCode() {
  return post<{ message: string; email: string }>(
    `${apiBaseUrl}/email-verification/send`,
    JSON.stringify({}),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function verifyCurrentEmail(verificationCode: string) {
  return post<{ message: string }>(
    `${apiBaseUrl}/email-verification/verify`,
    JSON.stringify({ verificationCode }),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function sendEmailChangeVerificationCode(newEmail: string) {
  return post<{ message: string; newEmail: string }>(
    `${apiBaseUrl}/user-email-change/send-code`,
    JSON.stringify({ newEmail }),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function verifyAndChangeEmail(data: {
  verificationCode: string;
  newEmail: string;
}) {
  return post<{ message: string; newEmail: string }>(
    `${apiBaseUrl}/user-email-change/verify-and-change`,
    JSON.stringify(data),
    {
      "Content-Type": "application/json",
    },
  );
}

export async function markUserForDeletion(userSub: string): Promise<{
  message: string;
  userProfile: UserProfile;
  isDeleted: boolean;
  deletionRequestedAt: string | null;
}> {
  return put<{
    message: string;
    userProfile: UserProfile;
    isDeleted: boolean;
    deletionRequestedAt: string | null;
  }>(`${apiBaseUrl}/user-deletion/${userSub}`, JSON.stringify({}));
}

/**
 * GET /api/user-deletion/:userSub
 * Owner endpoint to get a user's deletion status
 */
export async function getUserDeletionStatus(userSub: string) {
  return get<{
    isDeleted: boolean;
    deletionRequestedAt: string | null;
    permanentDeletionDate?: string;
    daysRemaining?: number;
    userProfile?: UserProfile;
  }>(`${apiBaseUrl}/user-deletion/${userSub}`);
}

/**
 * DELETE /api/user-deletion/:userSub
 * Owner endpoint to cancel a user's deletion request
 */
export async function cancelUserDeletion(userSub: string) {
  return deleteItem(`${apiBaseUrl}/user-deletion/${userSub}`);
}

export async function getProfileDefinitionsByTopic(topicId: string) {
  return get<ToolboxProfileDefinitionI[]>(
    `${apiBaseUrl}/topics/${topicId}/toolbox-profile-definitions`,
  );
}

export async function reorderProfileDefinitions(
  topicId: string,
  orderedDefinitionIds: string[],
) {
  return put<ToolboxProfileDefinitionI[]>(
    `${apiBaseUrl}/topics/${topicId}/toolbox-profile-definitions/reorder`,
    JSON.stringify({ orderedDefinitionIds }),
  );
}

export async function getActiveProfileDefinitionByTopic(topicId: string) {
  return get<ToolboxProfileDefinitionI>(
    `${apiBaseUrl}/topics/${topicId}/toolbox-profile-definitions/active`,
  );
}

export async function getProfileDefinition(definitionId: string) {
  return get<ToolboxProfileDefinitionI>(
    `${apiBaseUrl}/toolbox-profile-definitions/${definitionId}`,
  );
}

export async function createProfileDefinition(body: {
  topicId: string;
  questions?: ToolboxQuestionI[];
  displayName?: string | null;
}) {
  return post<ToolboxProfileDefinitionI>(
    `${apiBaseUrl}/toolbox-profile-definitions`,
    JSON.stringify(body),
    { "Content-Type": "application/json" },
  );
}

export type UpdateProfileDefinitionBody = {
  questions?: ToolboxQuestionI[];
  displayName?: string | null;
};

export async function updateProfileDefinition(
  definitionId: string,
  body: UpdateProfileDefinitionBody,
) {
  return put<ToolboxProfileDefinitionI>(
    `${apiBaseUrl}/toolbox-profile-definitions/${definitionId}`,
    JSON.stringify(body),
  );
}

export async function activateProfileDefinition(definitionId: string) {
  return patch<ToolboxProfileDefinitionI>(
    `${apiBaseUrl}/toolbox-profile-definitions/${definitionId}`,
    JSON.stringify({}),
  );
}

export async function deleteProfileDefinition(definitionId: string) {
  return deleteItem(
    `${apiBaseUrl}/toolbox-profile-definitions/${definitionId}`,
  );
}

export async function getToolboxPlan(planId: string) {
  return get<ToolboxPlanI>(`${apiBaseUrl}/plans/${planId}`);
}

export async function getToolboxTopicPlans(topicId: string) {
  return get<ToolboxPlanI[]>(`${apiBaseUrl}/topics/${topicId}/plans`);
}

export async function postToolboxTopicPlan(
  topicId: string,
  body: {
    goal: string;
    cue: string;
    timeframe: string;
  },
) {
  return post<ToolboxPlanI>(
    `${apiBaseUrl}/topics/${topicId}/plans`,
    JSON.stringify(body),
    { "Content-Type": "application/json" },
  );
}

export async function patchToolboxPlan(
  planId: string,
  body: {
    goal?: string;
    cue?: string;
    timeframe?: string;
  },
) {
  return patch<ToolboxPlanI>(
    `${apiBaseUrl}/plans/${planId}`,
    JSON.stringify(body),
  );
}

export async function deleteToolboxPlan(planId: string): Promise<void> {
  return deleteItem(`${apiBaseUrl}/plans/${planId}`);
}

export async function getUserToolboxProfile(topicId: string) {
  return get<UserToolboxProfileI>(
    `${apiBaseUrl}/topics/${topicId}/user-toolbox-profile`,
  );
}

export async function upsertUserToolboxProfile(
  topicId: string,
  answers: ToolboxAnswers,
) {
  return patch<UserToolboxProfileI>(
    `${apiBaseUrl}/topics/${topicId}/user-toolbox-profile`,
    JSON.stringify({ answers }),
  );
}

export async function getCheckinDefinitionsByTopic(topicId: string) {
  return get<ToolboxCheckinDefinitionI[]>(
    `${apiBaseUrl}/topics/${topicId}/toolbox-checkin-definitions`,
  );
}

export async function getActiveCheckinDefinitionByTopic(topicId: string) {
  return get<ToolboxCheckinDefinitionI>(
    `${apiBaseUrl}/topics/${topicId}/toolbox-checkin-definitions/active`,
  );
}

export async function getCheckinDefinitionById(definitionId: string) {
  return get<ToolboxCheckinDefinitionI>(
    `${apiBaseUrl}/toolbox-checkin-definitions/${definitionId}`,
  );
}

export async function activateCheckinDefinition(definitionId: string) {
  return patch<ToolboxCheckinDefinitionI>(
    `${apiBaseUrl}/toolbox-checkin-definitions/${definitionId}`,
    JSON.stringify({}),
  );
}

export async function updateCheckinDefinition(
  definitionId: string,
  body: {
    questions?: ToolboxQuestionI[];
    displayName?: string | null;
    ratingEnabled?: boolean;
    ratingScale?: number;
    ratingLabel?: string;
  },
) {
  return put<ToolboxCheckinDefinitionI>(
    `${apiBaseUrl}/toolbox-checkin-definitions/${definitionId}`,
    JSON.stringify(body),
  );
}

export async function createCheckinDefinition(body: {
  topicId: string;
  questions?: ToolboxQuestionI[];
  ratingEnabled?: boolean;
  ratingScale?: number;
  ratingLabel?: string;
}) {
  return post<ToolboxCheckinDefinitionI>(
    `${apiBaseUrl}/toolbox-checkin-definitions`,
    JSON.stringify(body),
    { "Content-Type": "application/json" },
  );
}

export async function deleteCheckinDefinition(definitionId: string) {
  return deleteItem(
    `${apiBaseUrl}/toolbox-checkin-definitions/${definitionId}`,
  );
}
